// Shared option lists + label helpers for the Subscribe popover, the admin
// Manage-recipients dialog, and the one-off Send dialog. Keeping them in one
// place so the day labels, ordinal suffixes, and attachment formats don't
// drift between the components.

export const FREQUENCY_OPTIONS = ["daily", "weekly", "monthly"] as const
export type Frequency = (typeof FREQUENCY_OPTIONS)[number]

// Attachment formats, shared by one-off sends and recurring subscriptions.
// The order is canonical and matches the server's allowlist
// (ReportSubscription::FORMATS / ReportSendsController#create).
export const FORMAT_OPTIONS = ["pdf", "csv", "xlsx"] as const
export type Format = (typeof FORMAT_OPTIONS)[number]

// Re-orders a selection canonically and drops anything the server would
// reject, so the same set always posts and displays identically.
export function normalizeFormats(formats: readonly string[]): Format[] {
  return FORMAT_OPTIONS.filter((option) => formats.includes(option))
}

export function formatsLabel(formats: readonly string[]): string {
  return normalizeFormats(formats)
    .map((format) => format.toUpperCase())
    .join(", ")
}

export const HOUR_OPTIONS: { value: number; label: string }[] = Array.from(
  { length: 24 },
  (_, h) => {
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    const ampm = h < 12 ? "AM" : "PM"
    return { value: h, label: `${hour12}:00 ${ampm}` }
  },
)

// 0 = Sunday, 6 = Saturday — matches Ruby's Date#wday so the FE picker
// value goes straight into the DB column without translation.
export const DAY_OF_WEEK_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Sundays" },
  { value: 1, label: "Mondays" },
  { value: 2, label: "Tuesdays" },
  { value: 3, label: "Wednesdays" },
  { value: 4, label: "Thursdays" },
  { value: 5, label: "Fridays" },
  { value: 6, label: "Saturdays" },
]

function ordinal(n: number): string {
  const lastTwo = n % 100
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

export const DAY_OF_MONTH_OPTIONS: { value: number; label: string }[] =
  Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: ordinal(i + 1),
  }))

export const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Phoenix",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "America/Mexico_City",
  "UTC",
  "Europe/London",
  "Europe/Berlin",
] as const

export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

export function dayOfWeekLabel(wday: number | null | undefined): string {
  if (wday === null || wday === undefined) return DAY_OF_WEEK_OPTIONS[1].label
  return DAY_OF_WEEK_OPTIONS[wday]?.label ?? DAY_OF_WEEK_OPTIONS[1].label
}

export function dayOfMonthLabel(day: number | null | undefined): string {
  if (day === null || day === undefined) return ordinal(1)
  return ordinal(day)
}

export function hourLabel(hour: number | null | undefined): string {
  return HOUR_OPTIONS[hour ?? 8]?.label ?? "8:00 AM"
}
